import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Package, ShoppingCart } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';
import { useProductPrices } from '../hooks/useProductPrices';
import { ProxyImage } from '../lib/imageProxy';
import { ProductCard } from '../components/product/ProductCard';

// Tipos
interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  image: string;
  parent_id?: string;
  subcategories?: Category[];
  parent?: Category;
}

interface Product {
  id: string;
  name: string;
  description: string;
  base_price: number;
  main_image: string;
  image?: string;
  stock: number;
  min_order: number;
  status: 'active' | 'inactive' | 'out_of_stock';
  prices?: {
    min_quantity: number;
    price: number;
    price_type?: string;
    offer_price?: number | null;
    offer_days?: number | null;
  }[];
  code?: string;
  categories?: { category: Category }[];
}

// Componentes auxiliares
const Breadcrumbs = ({ category, slug, subSlug }: { category: Category; slug: string; subSlug?: string }) => (
  <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
    <Link to="/" className="hover:text-gray-900">
      Inicio
    </Link>
    <ChevronRight className="w-4 h-4" />
    {subSlug ? (
      <>
        <Link to={`/category/${slug}`} className="hover:text-gray-900">
          {category.name}
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900">{category.name}</span>
      </>
    ) : (
      <span className="text-gray-900">{category.name}</span>
    )}
  </nav>
);

const CategoryHeader = ({ category }: { category: Category }) => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
    {/* Banner con fondo azul y título grande */}
    <div className="relative h-64 md:h-80 bg-blue-600 flex items-center justify-center">
      {/* Patrón sutil de fondo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      
      {/* Imagen de la categoría - Foto de perfil cuadrada */}
      <div className="absolute left-6 top-1/2 transform -translate-y-1/2 hidden md:block">
        <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-lg border-4 border-white overflow-hidden shadow-xl bg-white">
          <ProxyImage
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Categoría:+' + encodeURIComponent(category.name);
            }}
          />
        </div>
      </div>
      
      {/* Contenido centrado */}
      <div className="relative z-10 text-center px-6">
        {/* Imagen móvil - solo visible en pantallas pequeñas */}
        <div className="w-20 h-20 rounded-lg border-4 border-white overflow-hidden shadow-xl bg-white mx-auto mb-4 md:hidden">
          <ProxyImage
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Categoría:+' + encodeURIComponent(category.name);
            }}
          />
        </div>
        
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
          {category.name}
        </h1>
        <p className="text-blue-100 text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed">
          {category.description || `Explora nuestra colección de productos en la categoría ${category.name}`}
        </p>
      </div>
      
      {/* Decoración lateral */}
      <div className="absolute left-0 top-0 w-1 h-full bg-blue-400"></div>
      <div className="absolute right-0 top-0 w-1 h-full bg-blue-400"></div>
    </div>
    
    {/* Sección inferior con estadísticas */}
    <div className="px-6 py-6 bg-gray-50">
      <div className="flex flex-wrap justify-center gap-4">
        <div className="px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
          <span className="font-medium text-gray-700">{category.subcategories?.length || 0}</span>
          <span className="text-gray-600">Subcategorías</span>
        </div>
        <div className="px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-blue-600" />
          <span className="text-gray-700 font-medium">Productos disponibles</span>
        </div>
      </div>
    </div>
  </div>
);

const SubcategoryGrid = ({ 
  subcategories, 
  slug 
}: { 
  subcategories: Category[]; 
  slug: string; 
}) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
    {subcategories.map((subcategory) => (
      <Link
        key={subcategory.id}
        to={`/category/${slug}/${subcategory.slug}`}
        className="group"
      >
        <div className="relative h-48 rounded-lg overflow-hidden mb-2">
          <ProxyImage
            src={subcategory.image}
            alt={subcategory.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Subcategoría:+' + encodeURIComponent(subcategory.name);
            }}
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <h3 className="text-xl font-semibold text-white">{subcategory.name}</h3>
          </div>
        </div>
        <p className="text-sm text-gray-600">{subcategory.description}</p>
      </Link>
    ))}
  </div>
);

const EmptyProductsView = () => (
  <div className="text-center py-16">
    <div className="text-gray-400 mb-4">
      <Package className="w-16 h-16 mx-auto" />
    </div>
    <h3 className="text-xl font-medium text-gray-700 mb-2">No se encontraron productos</h3>
    <p className="text-gray-500 mb-6">
      No hay productos disponibles en esta categoría en este momento.
    </p>
    <Link
      to="/catalog"
      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
    >
      Ver catálogo completo
    </Link>
  </div>
);

const LoadingSkeleton = () => (
  <div className="max-w-7xl mx-auto px-4 py-8">
    <div className="animate-pulse">
      <div className="h-64 bg-gray-200 rounded-lg mb-8"></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i}>
            <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Componente principal
export default function Category() {
  const { slug, subSlug } = useParams<{ slug: string; subSlug?: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Usar hook centralizado de precios
  const productIds = useMemo(() => products.map(p => p.id), [products]);
  const { prices, loading: pricesLoading } = useProductPrices(productIds, {
    enabled: productIds.length > 0
  });

  const fetchCategoryAndProducts = async () => {
    try {
      // 1. Obtener la categoría
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', subSlug || slug)
        .single();

      if (categoryError) throw categoryError;
      
      // 2. Obtener subcategorías si existen
      if (categoryData) {
        const { data: subcategoriesData, error: subcategoriesError } = await supabase
          .from('categories')
          .select('id, name, slug, description, image')
          .eq('parent_id', categoryData.id);
          
        if (subcategoriesError) throw subcategoriesError;
        categoryData.subcategories = subcategoriesData || [];
      }
      
      setCategory(categoryData);

      // 3. Obtener productos asociados a la categoría mediante product_categories
      if (categoryData) {
        // Obtener los IDs de productos relacionados a la categoría
        const { data: productCatData, error: productCatError } = await supabase
          .from('product_categories')
          .select('product_id')
          .eq('category_id', categoryData.id);
        if (productCatError) throw productCatError;
        // Reflejar todos los productos asociados a la categoría
        const productIds = productCatData?.map((pc: any) => pc.product_id) || [];
        console.log('productIds', productIds);

        if (productIds.length > 0) {
          const { data: productsData, error: productsError } = await supabase
            .from('products')
            .select(`
              id, name, description, base_price, main_image,
              stock, min_order, status, code,
              categories:product_categories(category:categories(id, name, slug)),
              prices:product_prices(min_quantity, price, price_type, offer_price, offer_days)
            `)
            .in('id', productIds)
            .eq('status', 'active')
            .order('created_at', { ascending: false });

          console.log('productsData', productsData, 'productsError', productsError);

          if (productsError) throw productsError;
          
          if (productsData && productsData.length > 0) {
            const processedProducts = productsData.map(product => ({
              ...product,
              main_image: product.main_image || 'https://placehold.co/400x400?text=No+Image',
              categories: product.categories?.map((pc: any) => pc.category) || []
            }));
            
            setProducts(processedProducts);
          } else {
            setProducts([]);
          }
        } else {
          setProducts([]);
        }
      }
    } catch (error) {
      console.error('Error general al obtener datos:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar datos iniciales
  useEffect(() => {
    fetchCategoryAndProducts();
  }, [slug, subSlug]);

  // Nuevo efecto para hacer scroll al inicio cuando cambia la ruta
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, subSlug]);

  if (loading || pricesLoading) {
    return <LoadingSkeleton />;
  }

  if (!category) {
    return null;
  }

  return (
    <>
      {category && (
        <SEO
          title={category.name}
          description={`${category.description || `Productos de ${category.name}`}. Encuentra los mejores precios al por mayor en Guatemala.`}
          keywords={[
            category.name,
            'mayorista',
            'Guatemala',
            'productos al por mayor',
            category.parent?.name || '',
            ...(category.subcategories?.map(sub => sub.name) || [])
          ].filter(Boolean)}
          url={`/category/${category.slug}`}
          type="website"
        />
      )}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Breadcrumbs category={category} slug={slug!} subSlug={subSlug} />
        <CategoryHeader category={category} />

        {/* Mostrar subcategorías si estamos en una categoría principal y tiene subcategorías */}
        {!subSlug && category.subcategories?.length ? (
          <SubcategoryGrid 
            subcategories={category.subcategories} 
            slug={slug!} 
          />
        ) : null}

        {/* Mostrar productos o mensaje de categoría vacía */}
        {products.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => {
              const priceData = prices[product.id];

              return (
                <ProductCard
                  key={product.id}
                  product={{
                    id: product.id,
                    name: product.name,
                    main_image: product.main_image,
                    stock: product.stock,
                    min_order: product.min_order,
                    code: product.code
                  }}
                  priceInfo={priceData ? {
                    price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                    originalPrice: priceData.isOnSale ? priceData.price : null,
                    isOnSale: priceData.isOnSale,
                    discountPercentage: priceData.discountPercentage
                  } : undefined}
                  variant="catalog"
                  showCategory={false}
                  showCode={true}
                  showAddToCart={true}
                />
              );
            })}
          </div>
        ) : (
          <EmptyProductsView />
        )}
      </div>
    </>
  );
}